import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { Api } from 'src/smoothr-web-app-core/api/api';
import Order from 'src/smoothr-web-app-core/models/Order';
import Payment from 'src/smoothr-web-app-core/models/Payment';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { OrderUtils } from 'src/smoothr-web-app-core/utils/order-utils';
import { numberToCurrency, sleep } from 'src/smoothr-web-app-core/utils/utils';
import validator from 'validator';

@Component({
	selector: 'app-request-email',
	templateUrl: './request-email.component.html',
	styleUrls: ['./request-email.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class RequestEmailComponent {
	public payment: Payment;
	public venue: Venue;
	numberToCurrency = numberToCurrency;
	OrderUtils = OrderUtils;

	email = '';
	isValidEmail = true;
	loading = new BehaviorSubject(false);
	isSuccessPage = new BehaviorSubject(false);

	private modalCtrl = inject(ModalController);
	private snackbarCtrl = inject(MatSnackBar);
	private translate = inject(TranslateService);
	private cdr = inject(ChangeDetectorRef);

	constructor() {}

	static async show(modalCtrl: ModalController, payment: Payment, venue: Venue): Promise<boolean> {
		const modal = await modalCtrl.create({
			cssClass: 'auto-height item-modal',
			component: RequestEmailComponent,
			componentProps: {
				payment,
				venue,
			},
			showBackdrop: true,
			backdropDismiss: true,
		});
		await modal.present();

		const result = await modal.onDidDismiss();
		await sleep(100);
		return result.data;
	}

	dismiss() {
		this.modalCtrl.dismiss();
	}

	resetValidation() {
		this.isValidEmail = true;
	}

	validate() {
		if (!validator.isEmail(this.email)) {
			this.isValidEmail = false;
			return;
		} else {
			this.isValidEmail = true;
		}
	}

	async sendRequest() {
		this.validate();
		if (!this.isValidEmail) {
			this.snackbarCtrl.open(this.translate.instant('errors.email'), null, {
				duration: 5000,
			});
			return;
		}
		this.loading.next(true);
		try {
			const response = Api.sendCustomerInvoiceEmail(
				{ email: this.email.trim(), invoiceMailType: 'receipt' },
				this.payment._id,
				this.translate.currentLang
			);
			if (response) {
				await sleep(1000);
				this.isSuccessPage.next(true);
			}
			this.loading.next(false);
			await sleep(2000);

			await this.closeModalSuccess();
		} catch (e) {
			this.snackbarCtrl.open('Sorry, we have a problem with sending email...', null, {
				duration: 5000,
			});
			console.log(e);
			await this.closeModalError();
		}
	}
	async closeModalError() {
		this.modalCtrl.dismiss(false);
	}
	async closeModalSuccess() {
		await sleep(2000);
		this.cdr.markForCheck();

		this.modalCtrl.dismiss(true);
	}
}
